<template>
  <div class="asc-datepicker-input--container">
    <v-menu
      :ref="`asc-datepicker-menu-${_uid}`"
      v-model="menu"
      :content-class="getContentClass"
      :attach="attach"
      :top="top"
      :bottom="bottom"
      :left="left"
      :right="right"
      :nudge-top="hideDetails ? 0 : 23"
      :fixed="fixed"
      :allow-overflow="allowOverflow"
      :offset-overflow="offsetOverflow"
      :disabled="disabled"
      :z-index="zIndex"
      :close-on-content-click="closeOnContentClick"
      :open-on-click="openOnClick"
      :open-on-focus="openOnFocus"
      :open-on-hover="openOnHover"
      :tile="tile"
      :transition="transition"
      :origin="transitionOrigin"
      offset-y
      min-width="auto"
    >
      <template #activator="{ on, attrs }">
        <v-row
          :class="[
            'input-container',
            getHasIcon
              ? `hasicon iconpos--${
                  getIconPosition === 'right' ? 'right' : 'left'
                }`
              : '',
          ]"
          no-gutters
        >
          <v-btn
            v-if="hasIcon"
            :class="[
              'date-picker-btn',
              !menu ? 'v-btn--in-active' : 'v-btn--active',
              !iconAction || readonly ? 'v-btn--noAction' : '',
              floatFilter ? 'v-btn--float-filter' : '',
              dropDown ? 'v-btn--drop-down' : '',
            ]"
            text
            :ripple="false"
            :disabled="disabled"
            :readonly="readonly"
            v-on="iconAction || !readonly ? on : null"
          >
            <v-icon :size="`${getIconSize}px`">{{ getIcon }}</v-icon>
          </v-btn>
          <v-text-field
            :class="getDateInputFieldClass"
            :value="formatedDate"
            :label="label"
            :placeholder="placeholder"
            :color="getLabelColor"
            :disabled="disabled"
            :error="error"
            :rules="rules"
            :autofocus="autofocus"
            :hide-details="hideDetails"
            :required="required"
            :clearable="clearable"
            readonly
            v-bind="attrs"
            v-on="!readonly ? on : null"
            @click:clear="model = null"
          >
            <template #label>
              <asc-required-label :label="label" :required="required" />
            </template>
          </v-text-field>
        </v-row>
      </template>
      <v-row no-gutters>
        <v-col
          v-if="showFixedOptions"
          class="asc-datepicker-options--container"
        >
          <v-radio-group
            v-model="selectedOptionId"
            class="asc-datepicker-options--fixed"
            dense
            hide-details
            column
            @change="handleOptionSelection"
          >
            <v-radio
              v-for="option in getFixedDatePickerOptions"
              :key="option.id"
              :label="option.label"
              :value="option.id"
              :ripple="false"
            ></v-radio>
          </v-radio-group>
          <v-divider></v-divider>
          <v-radio-group
            v-model="selectedOptionId"
            class="asc-datepicker-options--custom"
            dense
            hide-details
            row
            @change="handleOptionSelection"
          >
            <v-radio
              v-for="option in getCustomDatePickerOptions"
              :key="option.id"
              :label="option.label"
              :value="option.id"
              :ripple="false"
            ></v-radio>
          </v-radio-group>
        </v-col>
        <v-col v-if="!isFixedOptionChecked" class="asc-datepicker--container">
          <v-date-picker
            v-model="model"
            class="asc-datepicker"
            :width="270"
            :value="new Date()"
            :disabled="disabled"
            :readonly="readonly"
            :no-title="true"
            :color="getThemeColor"
            :range="isRangePicker"
            :type="type"
            :allowed-dates="allowedDates"
            :max="getMax"
            :min="getMin"
            :multiple="multiple"
            :reactive="reactive"
            :scrollable="scrollable"
            :show-adjacent-months="showAdjacentMonths"
            :show-current="showCurrent"
            :show-week="showWeek"
            :flat="flat"
            @change="handleDatePick"
          ></v-date-picker>
        </v-col>
      </v-row>
    </v-menu>
  </div>
</template>

<script>
import { AscDatePickerMixin } from '@/components/mixins'
import AscRequiredLabel from '@/components/atoms/AscRequiredLabel'

export default {
  name: 'AscDatePicker',

  components: { AscRequiredLabel },

  mixins: [AscDatePickerMixin],
}
</script>

<style lang="scss">
.asc-datepicker-input--container {
  padding-top: 12px;
  margin-top: 4px;
  position: relative;

  .input-container {
    position: relative;

    &:hover {
      .date-picker-btn {
        &.v-btn {
          &--float-filter {
            color: $gray-800 !important;
          }
        }
      }
    }

    label {
      font-size: 12px;
      letter-spacing: 0;
      line-height: 18px;
    }

    &.hasicon {
      .date-picker-btn {
        position: absolute;
        z-index: 1;
        min-width: 18px;
        height: 18px;
        margin: 0;
        padding: 0;
        top: 7px;
        color: $gray-800;

        &.v-btn {
          &--in-active {
            color: $gray-500;
          }

          &--float-filter {
            top: 13px;

            &:not(.v-btn--in-active) {
              color: $accent-500 !important;
            }
          }

          &--drop-down {
            top: 12px;

            &:not(.v-btn--in-active) {
              color: $accent-500 !important;
              transform: rotate(-180deg);
            }
          }

          &--noAction {
            pointer-events: none;
          }
        }
      }

      &.iconpos {
        &--left {
          .date-picker-btn {
            left: 0;
          }

          .v-input {
            input {
              margin-left: 20px;
            }
          }
        }

        &--right {
          .date-picker-btn {
            right: 0;
            left: auto;

            &[readonly] {
              display: none;
            }
          }

          .v-input {
            input {
              margin-right: 20px;
            }
          }
        }
      }
    }

    .date-picker-field {
      margin: 0;
      padding: 0;
      font-size: 14px;
      line-height: 18px;
      letter-spacing: 0;

      .v-input__slot {
        &::before {
          border-width: 1px 0 0 0;
          border-color: $gray-400 !important;
        }

        &:hover::before {
          border-width: 1px 0 0 0;
          border-color: $gray-800 !important;
        }

        &::after {
          border-width: 1px 0 0 0 !important;
        }
      }

      &.v-input--is-nopick .v-input__slot {
        &::before {
          border-width: 0;
        }

        &::after {
          transform: scaleX(0);
        }
      }

      &.v-input--is-float-filter {
        .v-input__slot {
          input {
            font-size: 12px;
          }
        }
      }

      &.v-input--is-disabled .v-input__slot {
        cursor: not-allowed;

        input {
          cursor: not-allowed;
        }

        &::before {
          border-image: none;
        }
      }

      &.theme--light.v-input:not(.v-input--is-disabled) input {
        color: $primary-black;
      }

      .v-label {
        color: $gray-700;
        line-height: 18px;
        font-size: 12px;
        height: 18px;
        left: 0 !important;
        transform: translateY(-18px) scale(1);
      }

      .v-label,
      .v-label--active {
        transform: translateY(-18px) scale(1);
      }

      .v-input--selection-controls__input {
        margin-right: 0;
      }

      &.v-input-active-picker {
        color: $accent-500 !important;

        .v-input__slot::before {
          border-color: $accent-600;
        }

        .v-label {
          color: $accent-700;
          transform: translateY(-18px) scale(1);
          max-width: 133%;
          left: 0;
          right: auto;
          position: absolute;
        }
      }

      .error--text {
        color: $warning-base !important;
        caret-color: $warning-base !important;
      }
    }
  }
}

.asc-datepicker--menu {
  background-color: $primary-white;
  width: 270px;

  &.landscape {
    width: auto;

    .asc-datepicker--container {
      margin-top: 0;
    }
  }

  .asc-datepicker-options {
    &--container {
      min-width: 270px;
      margin: 14px 0 12.1px 0;

      .v-divider {
        margin: 8px 0 6px 0;
      }
    }

    &--fixed,
    &--custom {
      margin: 0 8px;
      padding: 0;

      .v-radio {
        .v-input--selection-controls__input {
          height: 16px;
          width: 16px;

          .v-icon.v-icon--dense {
            font-size: 16px;
          }
        }

        label.v-label {
          color: $primary-black;
          font-size: 14px;
          letter-spacing: 0;
          line-height: 18px;
        }
      }

      &.v-input--radio-group--column {
        .v-radio:not(:last-child):not(:only-child) {
          margin-bottom: 6px;
        }
      }
    }

    &--custom {
      position: relative;
      top: 4px;
    }
  }

  .asc-datepicker {
    .v-picker__body {
      .v-date-picker-header {
        &__value {
          button {
            font-weight: normal;
            font-size: 14px;
            letter-spacing: 0;
          }

          &:not(.v-date-picker-header__value--disabled) {
            button:not(:hover):not(:focus) {
              color: $primary-black;
            }
          }
        }
      }

      .v-date-picker-table {
        .v-btn {
          &.v-btn--active {
            color: $primary-black;

            &::before,
            &:hover::before {
              opacity: 0.18;
            }
          }

          &.v-date-picker-table__current {
            .v-btn__content {
              color: $primary-black;
            }
          }

          &.v-btn--rounded {
            border-radius: 0;
          }

          &::before {
            background-color: $accent-0;
          }

          &:hover {
            &::before {
              opacity: 1;
            }
          }
        }

        &.v-date-picker-table--month {
          .v-btn {
            border-radius: 0;

            .v-btn__content {
              font-weight: normal;
            }
          }
        }

        .v-date-picker-table--date__week,
        th {
          color: $primary-black;
          font-weight: normal;
          font-size: 12px;
        }
      }
    }

    .v-btn--floating {
      border-radius: 2px;

      &.v-btn--icon::before {
        border-radius: 0;
      }
    }
  }
}
</style>
